// ── RadarMapRenderer ─────────────────────────────────────────────────────────
// N-slot raster-tile renderer for animated radar playback on a Mapbox GL map.
//
// Creates one persistent Mapbox raster source + layer per unique scan frame.
// During playback, only layer opacity is toggled — zero tile re-fetches after
// the initial load.  This replaces the previous A/B dual-buffer that called
// setTiles() on every frame advance, which invalidated Mapbox's internal tile
// cache and forced re-fetching all visible tiles every 700ms.
//
// Public API:
//   constructor(map)     — map is a Mapbox GL Map instance
//   setFrame(frame)      — show the given frame (creates slot lazily if needed)
//   setFrames(frames)    — batch-create sources for all frames; remove stale ones
//   hideAll()            — hide all layers (used on product/site switch)
//   clear()              — remove all slots entirely (emergency reset)
//   destroy()            — remove all layers, sources and listeners

import { getAboveRadarAnchor } from '../mapbox/layers.js';

const CROSSFADE_MS  = 200;   // raster-opacity transition duration
const SOURCE_PREFIX = 'stormatlas-radar';

class RadarMapRenderer {
  constructor(map) {
    this._map       = map;
    this._destroyed = false;

    // N-slot state: one entry per unique scanId in the current manifest window.
    this._slots       = new Map(); // scanId → { sourceId, layerId, tileUrlTemplate }
    this._frontScanId = null;      // scanId of the currently visible frame

    // Zoom-reactive resampling.
    this._resamplingMode = null;
    this._zoomHandler    = () => this._syncResampling();
    this._map.on('zoom', this._zoomHandler);
    this._syncResampling();
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /** Show the given frame.  Creates a slot lazily if not pre-created by setFrames().
   *  Pass { immediate: true } to skip the 200ms crossfade on first-frame display. */
  setFrame(frame, { immediate = false } = {}) {
    if (this._destroyed || !this._map) return;
    if (!frame || typeof frame.scanId !== 'string' || typeof frame.tileUrlTemplate !== 'string') return;

    // Already showing this scan — nothing to do.
    if (frame.scanId === this._frontScanId) return;

    // Lazily create slot if not pre-created by setFrames().
    // If the same scanId exists with a different tile URL (reflectivity and
    // velocity share NEXRAD Level-II scanIds), remove the stale slot first
    // so the new product gets a fresh Mapbox source with the correct URL.
    if (!this._slots.has(frame.scanId)) {
      this._createSlot(frame);
    } else if (this._slots.get(frame.scanId).tileUrlTemplate !== frame.tileUrlTemplate) {
      this._removeSlot(frame.scanId);
      this._createSlot(frame);
    }

    // Hide the current front layer.
    if (this._frontScanId && this._slots.has(this._frontScanId)) {
      const old = this._slots.get(this._frontScanId);
      try { this._map.setPaintProperty(old.layerId, 'raster-opacity', 0); } catch {}
    }

    // Show the new front layer.
    const slot = this._slots.get(frame.scanId);
    if (slot) {
      // Skip 200ms crossfade for the initial frame on site switch — appear instantly.
      if (immediate) {
        try { this._map.setPaintProperty(slot.layerId, 'raster-opacity-transition',
              { duration: 0, delay: 0 }); } catch {}
      }
      try { this._map.setPaintProperty(slot.layerId, 'raster-opacity', 1); } catch {}
      this._frontScanId = frame.scanId;
      // Restore crossfade for subsequent frame switches during playback.
      if (immediate) {
        try { this._map.setPaintProperty(slot.layerId, 'raster-opacity-transition',
              { duration: CROSSFADE_MS, delay: 0 }); } catch {}
      }
    }
  }

  /**
   * Batch-create sources for all frames in the current manifest window.
   * Idempotent: existing slots with matching scanIds are left untouched.
   * Slots whose scanId is no longer in the incoming list are removed.
   */
  setFrames(frames) {
    if (this._destroyed || !this._map) return;
    if (!Array.isArray(frames)) return;

    const incoming = new Set();
    for (let i = 0; i < frames.length; i++) {
      if (frames[i]?.scanId) incoming.add(frames[i].scanId);
    }

    // Remove stale slots (frames that rolled out of the manifest window).
    for (const scanId of [...this._slots.keys()]) {
      if (!incoming.has(scanId)) {
        this._removeSlot(scanId);
      }
    }

    // Create slots for new frames; recreate if tile URL changed (product switch).
    for (let i = 0; i < frames.length; i++) {
      const f = frames[i];
      if (!f?.scanId) continue;
      if (!this._slots.has(f.scanId)) {
        this._createSlot(f);
      } else if (this._slots.get(f.scanId).tileUrlTemplate !== f.tileUrlTemplate) {
        this._removeSlot(f.scanId);
        this._createSlot(f);
      }
    }
  }

  /** Hide all layers (opacity 0).  Used on product/site switch to clear ghost tiles. */
  hideAll() {
    if (this._destroyed || !this._map) return;
    for (const { layerId } of this._slots.values()) {
      try { this._map.setPaintProperty(layerId, 'raster-opacity', 0); } catch {}
    }
    this._frontScanId = null;
  }

  /** Remove all slots (sources + layers).  For emergency recovery; normal
   *  product switches are handled by hideAll() + URL-mismatch detection in
   *  setFrame/setFrames, which avoids the blank-map gap that clear() causes. */
  clear() {
    if (this._destroyed || !this._map) return;
    for (const scanId of [...this._slots.keys()]) {
      this._removeSlot(scanId);
    }
    this._frontScanId = null;
  }

  /** Remove all layers, sources and listeners. */
  destroy() {
    if (this._zoomHandler && this._map) {
      try { this._map.off('zoom', this._zoomHandler); } catch {}
      this._zoomHandler = null;
    }
    for (const scanId of [...this._slots.keys()]) {
      this._removeSlot(scanId);
    }
    this._destroyed = true;
    this._map = null;
  }

  // ── Internal ──────────────────────────────────────────────────────────────

  _createSlot(frame) {
    const map      = this._map;
    const id       = this._sanitizeId(frame.scanId);
    const sourceId = `${SOURCE_PREFIX}-src-${id}`;
    const layerId  = `${SOURCE_PREFIX}-layer-${id}`;
    const [minz, maxz] = frame.tileZoomRange ?? [4, 8];

    try {
      map.addSource(sourceId, {
        type:     'raster',
        tiles:    [frame.tileUrlTemplate],
        tileSize: 512,
        minzoom:  minz,
        maxzoom:  maxz,
      });
      // Insert below warning/overlay layers so polygons remain visible above radar.
      // Prefer 'warnings-fill' (bottom of warning stack); fall back to
      // 'measure-line-glow'; fall back to the first road/label layer in the base
      // style so roads and city labels always render above radar.
      const _beforeId = map.getLayer('warnings-fill')    ? 'warnings-fill'
                      : map.getLayer('measure-line-glow') ? 'measure-line-glow'
                      : getAboveRadarAnchor(map);
      map.addLayer({
        id:     layerId,
        type:   'raster',
        source: sourceId,
        paint: {
          'raster-opacity':            0,
          'raster-opacity-transition': { duration: CROSSFADE_MS, delay: 0 },
          'raster-fade-duration':      0,
          'raster-resampling':         'linear',
          'raster-brightness-min':     0,
          'raster-contrast':           0.14,
          'raster-saturation':         0.22,
        },
      }, _beforeId);
      this._slots.set(frame.scanId, { sourceId, layerId, tileUrlTemplate: frame.tileUrlTemplate });
    } catch (err) {
      // If addSource succeeded but addLayer failed, remove the orphaned source.
      try { if (map.getSource(sourceId)) map.removeSource(sourceId); } catch {}
      console.error('[RadarMapRenderer] createSlot failed:', err);
    }
  }

  _removeSlot(scanId) {
    const slot = this._slots.get(scanId);
    if (!slot || !this._map) return;
    try { if (this._map.getLayer(slot.layerId))  this._map.removeLayer(slot.layerId); }  catch {}
    try { if (this._map.getSource(slot.sourceId)) this._map.removeSource(slot.sourceId); } catch {}
    this._slots.delete(scanId);
    if (this._frontScanId === scanId) this._frontScanId = null;
  }

  /** Mapbox source/layer IDs must be safe strings. */
  _sanitizeId(scanId) {
    return scanId.replace(/[^a-zA-Z0-9_-]/g, '_');
  }

  /** Sync resampling mode: linear at native tile zoom, nearest when overzoomed. */
  _syncResampling() {
    if (!this._map) return;
    const mode = this._map.getZoom() <= 8 ? 'linear' : 'nearest';
    if (mode === this._resamplingMode) return;
    this._resamplingMode = mode;
    for (const { layerId } of this._slots.values()) {
      try { this._map.setPaintProperty(layerId, 'raster-resampling', mode); } catch {}
    }
  }
}

export default RadarMapRenderer;
